import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../../utils/Helper";

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-[8px] border border-transparent bg-clip-padding text-[14px] font-medium leading-[16px] whitespace-nowrap transition-all outline-none select-none active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[16px]",
  {
    variants: {
      variant: {
        default:
          "bg-surface-action text-text-on-action [a]:hover:bg-surface-action/80 disabled:bg-surface-disabled! disabled:text-text-disabled",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
        "primary-outline":
          "text-text-action bg-transparent border-border-focused [&_svg]:text-icon-action",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        "secondary-outline":
          "text-text-body bg-surface-tertiary border-border-default hover:text-text-heading [&_svg]:hover:text-icon-default",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
        destructive: "bg-surface-error text-text-on-action",
        "destructive-outline":
          "bg-transparent border-border-error border text-text-error [&_svg]:text-icon-error",
        link: "text-primary underline-offset-4 hover:underline",
        pending: "bg-surface-information text-text-on-action",
        success: "bg-surface-success border-border-success text-text-on-action",
      },
      size: {
        default:
          "h-[40px] gap-[6px] px-[10px] has-data-[icon=inline-end]:pr-[8px] has-data-[icon=inline-start]:pl-[8px]",
        xs: "h-[24px] gap-[4px] rounded-[min(var(--radius-md),10px)] px-[8px] text-[12px] in-data-[slot=button-group]:rounded-[8px] has-data-[icon=inline-end]:pr-[6px] has-data-[icon=inline-start]:pl-[6px] [&_svg:not([class*='size-'])]:size-[12px]",
        sm: "h-[28px] gap-[4px] rounded-[6px] px-[12px] text-[14px] font-normal! in-data-[slot=button-group]:rounded-[8px] has-data-[icon=inline-end]:pr-[6px] has-data-[icon=inline-start]:pl-[6px] [&_svg:not([class*='size-'])]:size-[14px]",
        lg: "h-[36px] gap-[6px] px-[10px] has-data-[icon=inline-end]:pr-[8px] has-data-[icon=inline-start]:pl-[8px]",
        icon: "size-[32px]",
        "icon-xs":
          "size-[24px] rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-[8px] [&_svg:not([class*='size-'])]:size-[12px]",
        "icon-sm":
          "size-[28px] rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-[8px]",
        "icon-lg": "size-[36px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "default",
      size = "default",
      isLoading = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? <Loader2 className="animate-spin" /> : children}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
